<template>
	<div
		class="orderCard"
		:style="{ margin: `0 20px 20px calc(20px - ${scrollbarWidth}px)` }"
		:data-id-ref="data_id_ref"
	>
		<div class="orderDetails">
			<div class="info">
				<div>
					<div
						class="orderId text-truncate"
						data-toggle="tooltip"
						data-placement="top"
						:title="`${orderVal.order_number ?? '-'}`"
					>
						{{ orderVal.order_number ?? '-' }}
					</div>
					<div
						class="orderDate text-truncate"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						:title="`${formattedDate(orderVal.created_at)}`"
					>
						{{ formattedDate(orderVal.created_at) }}
					</div>
				</div>
				<div class="dropdown">
					<button
						v-show="!orderMenuLoading"
						class="btn btn-sm p-0 ripple optionsButton"
						type="button"
						id="dropdownMenuButton"
						data-bs-toggle="dropdown"
						aria-expanded="false"
					>
						<img height="20" width="20" :src="loadFile('arrowDown.svg')" />
					</button>
					<div v-if="orderMenuLoading" class="skeleton" style="height: 25px; width: 50%"></div>
					<div class="dropdown-menu p-3 ripple" aria-labelledby="dropdownMenuButton">
						<div
							:class="[
								'orderOptions',
								{ firstOption: index == 0, lastOption: index == orderOptions.length - 1 },
							]"
							v-for="(option, index) in orderOptions"
							:key="`${option.text} ${index}`"
							@click="option.action"
						>
							<span>{{ $t(option.text) }}</span>
							<div class="optionInageContainer">
								<img
									height="16"
									width="16"
									v-for="(file, index) in option.files"
									:key="`${file} - ${index}`"
									:src="loadFile(file)"
									:alt="file"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="orderItem.itemVisibility" class="orderItems">
				<div>
					<div
						class="itemName"
						data-toggle="tooltip"
						data-placement="top"
						:title="orderItem.title"
					>
						{{ orderItem.title }}
					</div>
					<div
						class="itemNumber text-truncate"
						data-toggle="tooltip"
						data-placement="top"
						:title="orderItem.sku"
					>
						{{ orderItem.sku }}
					</div>
				</div>
				<div class="itemCount">
					{{ orderItem.quantity }}
				</div>
			</div>
			<div
				:class="[
					'result',
					{
						withLink: [PaymentStatus.Valid, PaymentStatus.Expired].includes(
							paymentActions.status,
						),
						withoutLink: [PaymentStatus.Paid, PaymentStatus.Generate].includes(
							paymentActions.status,
						),
					},
				]"
			>
				<div class="price">
					<div class="priceTitle">{{ $t('Result') }}</div>
					<div
						class="priceValue text-truncate"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						:title="`${formatStringWithCommas(orderVal.order_total_amount)} ${
							orderVal.order_total_currency ?? ''
						}`"
					>
						{{ formatStringWithCommas(orderVal.order_total_amount) }}
						{{ orderVal.order_total_currency ?? '' }}
					</div>
				</div>
				<div class="status">
					<div
						data-toggle="tooltip"
						data-placement="top"
						:title="`${$t(
							paymentActions.status == PaymentStatus.Paid
								? 'Paid Invoice'
								: 'Pending Invoice',
						)}`"
						:class="[
							'text-truncate',
							{
								doneStatus: paymentActions.status == PaymentStatus.Paid,
								newStatus: paymentActions.status != PaymentStatus.Paid,
							},
						]"
					>
						{{
							$t(
								paymentActions.status == PaymentStatus.Paid
									? 'Paid Invoice'
									: 'Pending Invoice',
							)
						}}
					</div>
					<div
						data-toggle="tooltip"
						data-placement="top"
						:title="`${$t(orderVal.order_status) ? $t(orderVal.order_status) : '-'}`"
						:class="[
							'text-truncate',
							{
								newStatus: orderVal?.order_status != 'Done',
								doneStatus: orderVal?.order_status == 'Done',
							},
						]"
					>
						{{ $t(`order-status-enum.${orderVal.order_status}`) }}
					</div>
				</div>
			</div>
			<div
				class="orderShipmentContainer"
				@click="orderShipment"
				v-if="paymentActions.status == PaymentStatus.Paid && orderVal.order_status == 'Pending'"
			>
				<button :disabled="loading" type="button" class="btn ripple btn-sm btn-custom">
					<span>{{ $t('Order Shipment') }}</span>
				</button>
			</div>
			<div
				class="generateLinkButtonContainer"
				v-if="paymentActions.status == PaymentStatus.Generate"
			>
				<button
					@click="generatePaymentLink"
					:disabled="loading || orderVal.order_status == 'Cancelled'"
					type="button"
					class="btn ripple btn-sm btn-custom"
				>
					<span
						v-if="loading"
						class="spinner-border spinner-border-sm"
						role="status"
						aria-hidden="true"
					></span>
					<span v-if="!loading">{{ $t('Generate payment link') }}</span>
				</button>
			</div>
			<div
				class="withPaymentLink"
				v-if="[PaymentStatus.Valid, PaymentStatus.Expired].includes(paymentActions.status)"
			>
				<div>
					{{ $t('Payment Link') }} &nbsp;
					<img
						v-if="paymentActions.status == PaymentStatus.Valid"
						data-toggle="tooltip"
						data-placement="top"
						:title="`${$t('Available until')}${paymentActions.expiryDate}`"
						:src="loadFile('info.svg')"
						height="16"
						width="16"
					/>
				</div>
				<div class="input-group paymentActions">
					<input
						readonly
						v-model="paymentActions.link"
						:class="[{ expired: paymentActions.status == PaymentStatus.Expired }]"
						aria-label="paymentLink"
						class="form-control"
					/>
					<div v-if="paymentActions.status == PaymentStatus.Valid" class="input-group-append">
						<span @click="copyLink" class="input-group-text ripple">
							<img :src="loadFile('copy.svg')" height="14" width="14" />
						</span>
						<span
							@click="sendLink"
							:class="[
								'input-group-text',
								'addonSend',
								'ripple',
								{ arBorders: locale == 'en' },
								{ enBorders: locale == 'ar' },
							]"
						>
							<img :src="loadFile('send.svg')" height="14" width="14" />
						</span>
					</div>
					<div
						v-if="paymentActions.status == PaymentStatus.Expired"
						:class="['input-group-append', { noPointerEvents: loading }]"
					>
						<span
							@click="generatePaymentLink"
							:disabled="loading"
							:class="[
								'input-group-text',
								'addonSend',
								'ripple',
								{ arBorders: locale == 'en' },
								{ enBorders: locale == 'ar' },
							]"
						>
							<span
								v-if="loading"
								class="spinner-border spinner-border-sm"
								role="status"
								aria-hidden="true"
							></span>
							<img v-if="!loading" :src="loadFile('refresh.png')" height="14" width="14" />
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, reactive, inject, onMounted, watch, nextTick, computed } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import { Clipboard } from 'v-clipboard';
import { useStore } from 'vuex';
import { InvoiceStatus } from '@/components/whatsapp-managment/interfaces.js';
import { loadFile, formatStringWithCommas } from '@/components/utils.js';
import Invoice from '@/v2/modules/invoices/entities/invoice';
import { downloadBlob, uploadToFireStore } from '../../../../v2/utils/file-upload';
import { useAuthStore } from '../../../../v2/store/shared/auth-store';

const props = defineProps({
	order: Object,
	data_id_ref: Number,
	locale: String,
    tenant: [Number, String]
});

const store = useStore();
const authStore = useAuthStore();

const emit = defineEmits(['sendInvoice'])

const { t } = useI18n();

const swal = inject('$swal');

const PaymentStatus = Object.freeze({
	Paid: 'Paid',
	Valid: 'Valid',
	Expired: 'Expired',
	Generate: 'Generate',
});

const paymentActions = reactive({ link: '', status: PaymentStatus.Generate, expiryDate: '' });

const orderVal = reactive(props.order);

const base_url = window?.location?.origin;

const loading = ref(false);
const orderMenuLoading = ref(false);

const scrollbarWidth = ref(0);

const orderOptions = [
	{
		text: 'Show Order Details',
		files: ['arrow-up-left.svg'],
		action: () => {
			store.commit('Orders/setSelectedDetailsOrder', props.order);
		},
	},

	/* {
        text: 'Order Details PDF',
        files: ['download.svg', 'send.svg']
    },
    {
        text: 'Payment Bill PDF',
        files: ['download.svg', 'send.svg']
    },
    {
        text: 'Return Bill PDF',
        files: ['download.svg', 'send.svg']
    }, */
];
if (
	authStore.meta.isZatcaLinked &&
	props.order.financial_status === 'paid' &&
	props.order.marketplace_id == 8
) {
	orderOptions.push(
		...[
			{
				text: t('download-simple-tax-invoice'),
				files: [],
				action: async () => {
					orderMenuLoading.value = true;
					try {
						const id = props.order.id;
						const blob = await Invoice.generateOrderInvoice(id);
						const fileName = `simple-invoice-${id}`;
						downloadBlob(blob, fileName);
					} finally {
						orderMenuLoading.value = false;
					}
				},
			},
			{
				text: t('send-simple-tax-invoice-in-chat'),
				files: [],
				action: async () => {
					orderMenuLoading.value = true;

					try {
						const id = props.order.id;
						const blob = await Invoice.generateOrderInvoice(id);

						const fileName = `simple-invoice-${id}-${new Date().getTime()}`;
						const file = new File([blob], fileName, { type: 'application/pdf' });

						const { filePath } = await uploadToFireStore(
							file,
							`tenants/${props.tenant}/documents/${fileName}`,
						);
						console.log({ filePath });

						emit('sendInvoice', { filename: fileName, url: filePath });
					} finally {
						orderMenuLoading.value = false;
					}
				},
			},
		],
	);
}

const getItemProps = () => {
	const itemProps = { itemVisibility: false, title: '-', sku: '-', quantity: 0 };
	// Visibility
	if (Array.isArray(props.order?.main_order_items) && props.order?.main_order_items?.length > 0) {
		itemProps.itemVisibility = true;
		// Title
		const item = props.order?.main_order_items[0];
		const { translations } = item?.variant?.product ?? {};
		if (Array.isArray(translations) && translations.length > 0) {
			const localeTranslation = translations.find((elem) => elem?.locale == props.locale);
			itemProps.title = localeTranslation?.title ?? '-';
		}
		// SKU
		itemProps.sku = props.order?.main_order_items[0]?.variant?.vendor_stock_code ?? '-';
		itemProps.quantity = props.order?.main_order_items[0]?.quantity ?? 0;
	}
	return itemProps;
};

const sendLink = () => {
	store.commit('Chat/setChatMessage', { chatMessage: orderVal.invoices[0].invoice_url });
};

const getPaymentStatus = (order) => {
	const { invoices } = order ?? props?.order;
	if (!invoices || (invoices && !invoices.length)) {
		paymentActions.link = '';
		paymentActions.status = PaymentStatus.Generate;
		paymentActions.expiryDate = '';
		return;
	}
	const currentInvoice = invoices[0];
	const currentDate = new Date();
	const expiryDate = new Date(currentInvoice?.expiry_date);
	const formattedExpiryDate = formattedDate(expiryDate, true);
	paymentActions.expiryDate = formattedExpiryDate;

	if (currentInvoice?.invoice_status == InvoiceStatus.success) {
		paymentActions.link = '';
		paymentActions.status = PaymentStatus.Paid;
	}
	if (currentInvoice?.invoice_status == InvoiceStatus.pending) {
		if (currentDate.getTime() > expiryDate.getTime()) {
			paymentActions.link = t('Expired Link');
			paymentActions.status = PaymentStatus.Expired;
		} else {
			paymentActions.link = currentInvoice.invoice_url;
			paymentActions.status = PaymentStatus.Valid;
		}
	}
};

// Assuming this function is outside the component or in a utilities file
const formattedDate = (dateString, withHour = false) => {
	if (!dateString) return '-';

	const date = new Date(dateString);

	if (isNaN(date.getTime())) return '-';

	const options = {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		...(withHour && { hour: 'numeric', minute: 'numeric', hour12: false }),
	};
	const formatter = new Intl.DateTimeFormat('en-US', options);
	const parts = formatter.formatToParts(date).reduce((acc, part) => {
		if (part.type !== 'literal') {
			acc[part.type] = part.value;
		}
		return acc;
	}, {});

	const formattedString = withHour
		? `${t(parts.weekday)} ${parts.day} ${t(parts.month)} ${parts.year} ${parts.hour}:${
				parts.minute
		  }`
		: `${t(parts.weekday)} ${parts.day} ${t(parts.month)} ${parts.year}`;

	return formattedString;
};

const copyLink = () => {
	Clipboard.copy(orderVal?.invoices[0]?.invoice_url);
};

const orderShipment = () => {
	window.open(`${base_url}/orders/index?filter[order_number]=${orderVal.order_number}`, '_blank');
};

const generatePaymentLink = async () => {
	try {
		loading.value = true;
		const response = await axios.get(
			`${base_url}/api/main-order/payment/generate-link/${orderVal.id}`,
		);

		if (response?.data?.success) {
			// Prepare back up expiry date 2 days from now in case the resposnse doesn't have expiry date
			const now = new Date();
			const backUpExpiryDate = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
			const year = backUpExpiryDate.getFullYear();
			const month = backUpExpiryDate.getMonth() + 1;
			const day = backUpExpiryDate.getDate();
			const hours = backUpExpiryDate.getHours();
			const minutes = backUpExpiryDate.getMinutes();
			const seconds = backUpExpiryDate.getSeconds();

			const formattedBackUpExpiryDateDate = `${year}-${month.toString().padStart(2, '0')}-${day
				.toString()
				.padStart(2, '0')}T${hours.toString().padStart(2, '0')}:${minutes
				.toString()
				.padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

			const expiryDate = response?.data?.expiry_date ?? formattedBackUpExpiryDateDate;
			// update order with new invoice
			orderVal.invoices = [
				{
					invoice_url: response.data.link,
					invoice_status: InvoiceStatus.pending,
					expiry_date: expiryDate,
				},
			];
			getPaymentStatus(orderVal);
		} else {
			swal.fire({
				title: response?.data?.msg ?? $t('Error Msg'),
				allowOutsideClick: false,
				confirmButtonText: t('ok'),
			});
		}
		loading.value = false;
	} catch (error) {
		loading.value = false;
		console.error('Error generating payment link:', error);
	}
};

const orderItem = computed(() => getItemProps());

watch(
	() => props.order,
	(newVal) => {
		getPaymentStatus(newVal);
	},
	{ deep: true },
);

onMounted(() => {
	getPaymentStatus();
	// Get orders padding
	nextTick().then(() => {
		const ordersContainer = $('#ordersContainer');
		if (ordersContainer.get(0)) {
			scrollbarWidth.value =
				ordersContainer.get(0).offsetWidth - ordersContainer.get(0).clientWidth;
		}
	});
});
</script>

<style scoped lang="scss">
@import './styles/order.scss';
</style>
